import { setupRcdPhoneInputs } from "../core/components/phone-input";
import { setupRcdDropdownKits } from "../core/components/dropdown-kit";
import { renderRcdIcons } from "../core/icons";

const setupInquiryAjaxPage = () => {
    const ajaxRoot = document.querySelector("[data-inquiry-list-surface]");
    if (!ajaxRoot || ajaxRoot.dataset.ajaxReady === "true") {
        return;
    }

    ajaxRoot.dataset.ajaxReady = "true";
    let searchTimer = null;
    let activeRequest = null;

    const toUrl = (urlLike) => {
        const url = new URL(urlLike, window.location.origin);
        url.pathname = "/Inquiries";
        url.searchParams.delete("handler");
        return url;
    };

    const swapSurface = (html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const nextSurface = doc.querySelector("[data-inquiry-list-surface]");
        const currentSurface = document.querySelector("[data-inquiry-list-surface]");
        if (!nextSurface || !currentSurface) {
            window.location.reload();
            return;
        }

        currentSurface.replaceWith(nextSurface);
        window.Alpine?.initTree?.(nextSurface);
        renderRcdIcons();
        setupRcdDropdownKits(nextSurface, renderRcdIcons);
        setupInquiryAjaxPage();
        setupInquiryBulkActions();
        setupInquiryAjaxForms();
        setupInquiryStackLinks();
        setupRcdPhoneInputs();
    };

    const load = async (urlLike, { push = true } = {}) => {
        const url = toUrl(urlLike);
        const currentSurface = document.querySelector("[data-inquiry-list-surface]");
        if (!currentSurface) {
            window.location.assign(url);
            return;
        }

        activeRequest?.abort();
        activeRequest = new AbortController();
        currentSurface.setAttribute("aria-busy", "true");
        currentSurface.classList.add("is-loading");

        try {
            const response = await fetch(url, {
                credentials: "same-origin",
                signal: activeRequest.signal,
                headers: {
                    "Accept": "text/html",
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok) {
                throw new Error("Inquiry list request failed");
            }

            swapSurface(await response.text());
            if (push) {
                window.history.pushState({ inquiryAjax: true }, "", url);
            }
        } catch (error) {
            if (error.name !== "AbortError") {
                window.location.assign(url);
            }
        } finally {
            currentSurface.removeAttribute("aria-busy");
            currentSurface.classList.remove("is-loading");
        }
    };

    window.rcdLoadInquiryList = load;

    ajaxRoot.addEventListener("click", (event) => {
        const link = event.target.closest("[data-inquiry-ajax-link]");
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();
        load(link.href);
    });

    ajaxRoot.addEventListener("submit", (event) => {
        const form = event.target.closest("[data-inquiry-search-form]");
        if (!form) {
            return;
        }

        event.preventDefault();
        window.clearTimeout(searchTimer);
        load(`?${new URLSearchParams(new FormData(form)).toString()}`);
    });

    ajaxRoot.addEventListener("input", (event) => {
        const input = event.target.closest("[data-inquiry-search-form] input[type='search']");
        if (!input) {
            return;
        }

        const form = input.form;
        form.elements.pageNumber.value = "1";
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
            load(`?${new URLSearchParams(new FormData(form)).toString()}`);
        }, 2000);
    });

    ajaxRoot.addEventListener("change", (event) => {
        const select = event.target.closest("[data-inquiry-page-size]");
        if (!select) {
            return;
        }

        event.preventDefault();
        const form = select.form;
        form.elements.pageNumber.value = "1";
        load(`?${new URLSearchParams(new FormData(form)).toString()}`);
    });

    if (window.__rcdInquiryAjaxPopstateReady !== true) {
        window.__rcdInquiryAjaxPopstateReady = true;
        window.addEventListener("popstate", () => {
            load(window.location.href, { push: false });
        });
    }
};

const setupInquiryStackLinks = () => {
    if (window.__rcdInquiryStackLinksReady === true) {
        return;
    }

    window.__rcdInquiryStackLinksReady = true;
    document.addEventListener("click", (event) => {
        const link = event.target.closest("[data-inquiry-stack-link]");
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();
        replaceInquiryStack(link.href).catch(() => {
            window.location.assign(link.href);
        });
    });
};

const setupInquiryAjaxForms = () => {
    document.querySelectorAll("[data-inquiry-ajax-form]").forEach((form) => {
        if (form.dataset.ajaxFormReady === "true") {
            return;
        }

        form.dataset.ajaxFormReady = "true";
        form.addEventListener("submit", (event) => {
            event.preventDefault();
            saveInquiryForm(form, event.submitter);
        });
    });
};

const saveInquiryForm = async (form, submitter) => {
    const id = form.dataset.inquiryId;
    if (!id) {
        form.submit();
        return;
    }

    const mode = submitter?.dataset?.inquirySaveMode || "exit";
    const status = ensureInquiryAjaxStatus(form);
    const actionBar = form.nextElementSibling;
    const buttons = actionBar?.querySelectorAll("button, a") || [];

    form.classList.add("is-saving");
    buttons.forEach((button) => {
        if ("disabled" in button) {
            button.disabled = true;
        }
    });
    status.textContent = "Saving enquiry...";
    status.dataset.state = "loading";

    try {
        const response = await fetch(`/api/workspace/inquiries/${encodeURIComponent(id)}`, {
            method: "PUT",
            credentials: "same-origin",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest"
            },
            body: JSON.stringify(buildInquiryPayload(form))
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(data?.detail || data?.title || "Enquiry could not be saved.");
        }

        status.textContent = data?.emailCopyRequested
            ? "Enquiry saved and customer copy processed."
            : "Enquiry saved.";
        status.dataset.state = "success";
        window.rcdUser?.toast?.({
            type: "success",
            title: "Saved",
            message: status.textContent
        });

        if (mode === "continue") {
            return;
        }

        await replaceInquiryStack(form.dataset.listUrl || "/Inquiries");
    } catch (error) {
        status.textContent = error.message || "Enquiry could not be saved.";
        status.dataset.state = "error";
        window.rcdUser?.toast?.({
            type: "danger",
            title: "Save failed",
            message: status.textContent
        });
    } finally {
        form.classList.remove("is-saving");
        buttons.forEach((button) => {
            if ("disabled" in button) {
                button.disabled = false;
            }
        });
    }
};

const ensureInquiryAjaxStatus = (form) => {
    const actionBar = form.nextElementSibling;
    const actionStatus = actionBar?.querySelector("[data-inquiry-ajax-status]");
    if (actionStatus) {
        return actionStatus;
    }

    const formStatus = form.querySelector("[data-inquiry-ajax-status]");
    if (formStatus && actionBar) {
        actionBar.insertBefore(formStatus, actionBar.querySelector(".user-inquiry-dialog__actions"));
        return formStatus;
    }

    if (formStatus) {
        return formStatus;
    }

    const status = document.createElement("p");
    status.className = "user-ajax-status";
    status.dataset.inquiryAjaxStatus = "";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    if (actionBar) {
        actionBar.insertBefore(status, actionBar.querySelector(".user-inquiry-dialog__actions"));
        return status;
    }

    form.appendChild(status);
    return status;
};

const buildInquiryPayload = (form) => {
    const data = new FormData(form);
    const read = (name) => {
        const value = data.get(name);
        return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
    };
    const source = read("CreateInput.Source") || "Manual";

    return {
        customerName: read("CreateInput.CustomerName") || "",
        email: read("CreateInput.Email"),
        phoneNumber: read("CreateInput.PhoneNumber"),
        description: read("CreateInput.Description"),
        addressLine1: read("CreateInput.AddressLine1"),
        source: inquirySourceToNumber(source),
        sendCopyToCustomer: data.get("CreateInput.SendCopyToCustomer") === "true" ||
            data.get("CreateInput.SendCopyToCustomer") === "on"
    };
};

const inquirySourceToNumber = (source) => {
    const sourceMap = {
        Manual: 0,
        WebsiteEmbed: 1,
        Phone: 2,
        Email: 3,
        Referral: 4,
        Social: 5,
        WalkIn: 6
    };

    return sourceMap[source] ?? 0;
};

const replaceInquiryStack = async (urlLike) => {
    const url = new URL(urlLike, window.location.origin);
    url.pathname = "/Inquiries";
    url.searchParams.delete("handler");
    const currentStack = document.querySelector(".rcd-stack");
    const currentSurface = document.querySelector("[data-inquiry-list-surface]");
    const currentDetailForm = document.querySelector("[data-inquiry-ajax-form]");

    currentSurface?.classList.add("is-loading");
    currentSurface?.setAttribute("aria-busy", "true");
    currentDetailForm?.classList.add("is-saving");

    const response = await fetch(url, {
        credentials: "same-origin",
        headers: {
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest"
        }
    });

    if (!response.ok) {
        window.location.assign(url);
        return;
    }

    const doc = new DOMParser().parseFromString(await response.text(), "text/html");
    const nextStack = doc.querySelector(".rcd-stack");
    if (!nextStack || !currentStack) {
        window.location.assign(url);
        return;
    }

    currentStack.replaceWith(nextStack);
    window.history.pushState({ inquiryAjax: true }, "", url);
    window.Alpine?.initTree?.(nextStack);
    renderRcdIcons();
    setupInquiryAjaxPage();
    setupInquiryBulkActions();
    setupInquiryAjaxForms();
    setupInquiryStackLinks();
    setupRcdPhoneInputs();
};

window.rcdInquiryClose = (urlLike) => {
    replaceInquiryStack(urlLike).catch(() => {
        window.location.assign(urlLike);
    });
};

const setupInquiryBulkActions = () => {
    if (window.__rcdInquiryBulkActionsReady === true) {
        syncInquiryBulkSelection();
        return;
    }

    window.__rcdInquiryBulkActionsReady = true;

    document.addEventListener("change", (event) => {
        const selectAll = event.target.closest("[data-inquiry-select-all]");
        if (selectAll) {
            document.querySelectorAll("[data-inquiry-select]").forEach((checkbox) => {
                checkbox.checked = selectAll.checked;
            });
            syncInquiryBulkSelection();
            return;
        }

        if (event.target.closest("[data-inquiry-select]")) {
            syncInquiryBulkSelection();
        }
    });

    document.addEventListener("submit", (event) => {
        if (event.target.closest("[data-inquiry-bulk-form]")) {
            syncInquiryBulkSelection();
        }
    });

    syncInquiryBulkSelection();
};

const syncInquiryBulkSelection = () => {
    const selected = Array.from(document.querySelectorAll("[data-inquiry-select]:checked"));
    const all = Array.from(document.querySelectorAll("[data-inquiry-select]"));
    const selectAll = document.querySelector("[data-inquiry-select-all]");
    const container = document.querySelector("[data-inquiry-bulk-selected-container]");
    const actions = document.querySelectorAll("[data-inquiry-bulk-action]");

    if (container) {
        container.replaceChildren();
        selected.forEach((checkbox) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = container.dataset.bulkInputName || "selectedInquiryIds";
            input.value = checkbox.value;
            container.appendChild(input);
        });
    }

    actions.forEach((button) => {
        button.disabled = selected.length === 0;
    });

    if (selectAll) {
        selectAll.checked = selected.length > 0 && selected.length === all.length;
        selectAll.indeterminate = selected.length > 0 && selected.length < all.length;
    }
};

export {
    setupInquiryAjaxPage,
    setupInquiryBulkActions,
    setupInquiryAjaxForms,
    setupInquiryStackLinks
};
